<template>
  <main v-if="data" class="container">
    <div class="row">
      <div class="col-12 col-xs-10 col-md-12 col-lg-10 col-xl-8 col-xxl-7 mx-auto mt-4 mt-md-5">
        <div>
          <h1 class="text-start">
            <span class="inherit badge bg-primary rounded-pill mb-2">{{ data.registration }}</span>
            <span class="h2 d-block">{{ ' ' + data.model }}</span>
          </h1>
          <p v-if="data.nickname" class="text-secondary fst-italic mb-0">Bijnaam: {{ data.nickname }}</p>
        </div>
        <div class="row mt-3">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Over {{ data.registration }} - {{ data.model }}
            </h2>
            <div class="row g-2 mb-3">
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Registratie</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.registration }}
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Serial</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.serial ?? '-' }}
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Model</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ data.model }}
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Status</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    Actief
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Leeftijd</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.age ? formatAge(data.age) + ' jaar' : '-' }}
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Laatste vlucht</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ lastFlight ? formatDate(lastFlight.scheduled_departure) : '-' }}
                    <span class="small opacity-75" v-if="lastFlight">({{ lastFlight.flight_number }})</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Luchtvaartmaatschappij
            </h2>
            <div class="row g-2 mb-3">
              <div class="col-12 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">IATA</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    <NuxtImg
                        height="18"
                        v-if="data.owner.logo_small_url"
                        class="me-1 mt-n1"
                        loading="lazy"
                        :src="data.owner.logo_small_url"
                        :alt="data.owner.name + ' icon'"
                    />
                    <NuxtLink
                        :to="{ name: 'nl-luchtvaartmaatschappijen-slug', params: { slug: data.owner.slug }}"
                        class="inherit fw-bold ms-1"
                        :title="data.owner.name">
                      {{ data.owner.short_name ?? data.owner.name }}
                    </NuxtLink>
                  </p>
                </div>
              </div>
              <div class="col-12 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">ICAO</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.owner.icao }}
                  </p>
                </div>
              </div>
              <div v-if="data.owner.country" class="col-12 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Land</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.owner.country.emoji }} {{ data.owner.country.name_nl }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="data.flights.filter(flight => flight.is_live).length" class="row mb-4">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Live Vluchten
            </h2>
          </div>
          <flight-table
              v-if="data.flights.length"
              :limit="3"
              :live-only="true"
              :show-load-more="true"
              :flights="data.flights.filter(flight => flight.is_live).reverse()"
          />
        </div>

        <div v-else-if="data.flights.filter(flight => !flight.is_landed).length" class="row mb-4">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Aankomende vluchten
            </h2>
          </div>
          <flight-table
              v-if="data.flights.length"
              :limit="3"
              :show-load-more="true"
              :flights="data.flights.filter(flight => flight.status === 'Scheduled').reverse()"
          />
        </div>

        <div v-if="data.flights.length" class="row mb-4">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Recente vluchten
            </h2>
            <div v-if="data.owner.frequent_flyer_program !== 'Flying Blue'" class="alert alert-warning">
              <p class="small mb-0"><i class="fa-solid fa-circle-info ps-1 pe-1"></i> We tonen alleen Flying Blue gerelateerde vluchten van dit vliegtuig.</p>
            </div>
          </div>
          <flight-table
              v-if="data.flights.length"
              :show-load-more="false"
              :flights="data.flights.filter(flight => flight.status !== 'Scheduled' && !flight.is_live)"
          />
        </div>

        <div v-if="data.stats && data.stats.top_destinations" class="row mb-2">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Populaire bestemmingen
            </h2>
            <div class="card no-hover bg-blurry mb-2 p-3">
              <p class="mb-0 opacity-75 small">
                <i class="fa-solid fa-circle-info ps-1 pe-1"></i> {{ data.registration }} bestemmingen in de afgelopen <u>30 dagen</u>:
              </p>
            </div>
            <div v-if="data.owner.frequent_flyer_program !== 'Flying Blue'" class="alert alert-warning mb-2">
              <p class="small mb-0">We tellen alleen Flying Blue gerelateerde vluchten van dit vliegtuig.</p>
            </div>
            <div class="row g-2 mb-3">
              <div class="col-12 col-sm-6 col-md-4" v-for="destination in data.stats.top_destinations">
                <div class="card no-hover d-flex flex-row align-items-center p-3 bg-blurry">
                  <div>
                    <p class="mb-0 index bg-blurry border fw-bold">
                      {{ destination.count }}x
                    </p>
                  </div>
                  <div>
                    <p class="mb-1 small text-uppercase opacity-75 fw-normal truncate">{{ destination.destination.city.country.name_nl }}</p>
                    <h3 class="mb-0 h6 fw-bold mb-n1 truncate">
                      {{ destination.destination.city.country.emoji }} {{ destination.destination.city.name_nl }}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="data.stats && data.stats.total > 0" class="row mb-4">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              {{ data.registration }} Statistieken
            </h2>
            <div class="card no-hover bg-blurry mb-2 p-3">
              <p class="mb-0 opacity-75 small">
                <i class="fa-solid fa-circle-info ps-1 pe-1"></i> {{ data.registration }} statistieken in de afgelopen <u>30 dagen</u>:
              </p>
            </div>
            <div class="row g-2 mb-3">
              <div class="col-6 col-md-4" >
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Vluchten</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Intl.NumberFormat("nl").format(data.stats.total) }} ({{ Intl.NumberFormat("nl", { maximumSignificantDigits: 2 }).format(data.stats.total / 30) }} per dag)
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4" >
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Operationele uren</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Math.round(data.stats.total_duration / 60) }} ({{ Intl.NumberFormat("nl", { maximumSignificantDigits: 2 }).format(data.stats.avg_duration / 60) }} per vlucht)
                  </p>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Afstand
                  <span role="button" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Gebaseerd op oorspronkelijk vluchtroute"><i class="fa-solid fa-circle-info ps-1 pe-1"></i></span></h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Intl.NumberFormat("nl", { maximumSignificantDigits: 1 }).format(data.stats.total_distance) }}km ({{ Intl.NumberFormat("nl", { maximumSignificantDigits: 2 }).format(data.stats.avg_distance) }} per vlucht)
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Vertraagde vertrekken</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Intl.NumberFormat("nl").format(data.stats.total_flights_departure_delay) }} ({{ 100 - data.stats.avg_flights_depart_on_time}}% op tijd)
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Vertraagde aankomsten</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Intl.NumberFormat("nl").format(data.stats.total_flights_arrival_delay) }} ({{ 100 - data.stats.avg_flights_arrive_on_time}}% op tijd)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-6 gy-2" itemscope itemtype="https://schema.org/FAQPage">
          <div class="col-12">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Veelgestelde vragen</h2>
          </div>
          <Question>
            <template v-slot:question>
              Wat is het registratienummer van dit vliegtuig?
            </template>
            <template v-slot:answer>
              <p class="mb-0">Het registratienummer van dit {{ data.manufacturer }} vliegtuig is {{ data.registration }}.</p>
            </template>
          </Question>
          <Question v-if="data.owner">
            <template v-slot:question>
              Welke luchtvaartmaatschappij bezit dit vliegtuig?
            </template>
            <template v-slot:answer>
              <p class="mb-0">Dit vliegtuig is eigendom van {{ data.owner.name }}.</p>
            </template>
          </Question>
          <Question>
            <template v-slot:question>
              Wat is het type en model van dit vliegtuig?
            </template>
            <template v-slot:answer>
              <p class="mb-0">De {{ data.registration }} van {{ data.owner?.name }} is een {{ data.model }}.</p>
            </template>
          </Question>
          <Question v-if="data.flights">
            <template v-slot:question>
              Wat zijn de recente bestemmingen en historie van dit vliegtuig?
            </template>
            <template v-slot:answer>
              <p>De recente bestemmingen van deze {{ data.registration }} {{ data.manufacturer }} zijn:</p>
              <ul class="mb-0">
                <li v-for="flights in data.flights">
                  {{ flights.flight_number }} van {{ flights.origin.city.name_nl }} ({{ flights.origin.iata }}) naar {{ flights.destination.city.name_nl }} ({{ flights.destination.iata }})
                </li>
              </ul>
            </template>
          </Question>
          <Question v-if="data.age">
            <template v-slot:question>
              Hoe oud is dit vliegtuig?
            </template>
            <template v-slot:answer>
              <p class="mb-0">Deze {{ data.manufacturer }} vliegtuig van {{ data.owner?.name }} is {{ formatAge(data.age) }} jaar oud.</p>
              <p v-if="formatAge(data.age) > 10" class="mt-2">De veiligheid van vliegtuigen staat altijd voorop bij {{ data.owner?.name }}, ongeacht de leeftijd van het toestel. Deze {{ data.manufacturer }} van {{ formatAge(data.age) }} jaar is veilig en is nog niet toe aan zijn pensioen. Bovendien wordt het interieur vaker geüpgraded met nieuwe stoelen dan dat er een nieuw vliegtuig wordt besteld, voor extra comfort.</p>
            </template>
          </Question>
          <Question v-if="data.stats && data.stats.top_destinations">
            <template v-slot:question>
              Welke bestemmingen vliegt {{ data.owner?.short_name ?? data.owner?.name ?? 'de luchtvaarmaatschappij' }} met de {{ data.manufacturer}} {{ data.registration }}?
            </template>
            <template v-slot:answer>
              <p class="mb-2">
                In de afgelopen 30 dagen heeft de {{ data.registration }} van {{ data.owner?.short_name ?? data.owner?.name ?? 'de luchtvaarmaatschappij' }} {{ data.stats?.total ?? 0 }} vluchten gemaakt naar verschillende bestemmingen. De {{ data.stats.top_destinations.length }} populairste {{ data.registration }} bestemmingen waren:
              </p>
              <ul>
                <li v-for="destination in data.stats.top_destinations">
                  {{ destination.count }} keer naar {{ destination.destination.city.name_nl }} (<nuxtLink :title="(data.owner?.short_name ?? data.owner?.name ?? '') + ' vluchten naar ' + destination.destination.name + ' (' + destination.destination.city.name_nl + ')'" :to="{ name: 'vluchten-naar', params: { name: destination.destination.city.slug_nl, iata: destination.destination.iata.toLowerCase() } }">{{ destination.destination.name }}</nuxtLink>).
                </li>
              </ul>
              <p class="mb-0 small">Bij onze berekeningen houden we uitsluitend rekening met commerciële Flying Blue-gerelateerde vluchten waarbij {{ data.registration }} de bestemming heeft bereikt.</p>
            </template>
          </Question>
        </div>
      </div>
    </div>
  </main>
</template>
<script setup lang="ts">
import moment from "moment/moment";
import 'moment/locale/nl';

const route = useRoute()
const runtimeConfig = useRuntimeConfig()

const {data: fetchedData} = await useAsyncData(
    'aircraft-' + route.params.slug,
    async () => {
      return $fetch(runtimeConfig.public.backendUrl + "/api/v2/aircrafts/" + route.params.slug, {
        headers: {
          'User-Agent': 'RewardsFlightsFrontend/1.0 (https://rewards.flights)',
        },
        params: {
          include: 'stats',
        }
      })
    }
);

if (!fetchedData.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Vliegtuig niet gevonden'
  })
}

const data = ref(fetchedData.value.aircraft);

defineOgImage({
  component: 'route',
  title: data.value.model,
  description: data.value.registration,
});

useSeoMeta({
  title: data.value.registration + ' - ' + data.value.model + ' van ' + data.value.owner.name,
  ogTitle: data.value.registration + ' - ' + data.value.model + ' van ' + data.value.owner.name,
  twitterTitle: data.value.registration + ' - ' + data.value.model + ' van ' + data.value.owner.name,
  description: 'Ontdek de ' + data.value.model + ' van ' + data.value.owner.name + ' met registratie ' + data.value.registration + ' (' + data.value.serial + '). Bekijk vluchten, geschiedenis, seat map en meer van dit vliegtuig.',
  ogDescription: 'Ontdek de ' + data.value.model + ' van ' + data.value.owner.name + ' met registratie ' + data.value.registration + ' (' + data.value.serial + '). Bekijk vluchten, geschiedenis, seat map en meer van dit vliegtuig.',
  twitterDescription: 'Ontdek de ' + data.value.model + ' van ' + data.value.owner.name + ' met registratie ' + data.value.registration + ' (' + data.value.serial + '). Bekijk vluchten, geschiedenis, seat map en meer van dit vliegtuig.',
  ogUrl: runtimeConfig.public.baseUrl + "/nl/vliegtuigen/" + data.value.slug,
  ogImage: data.value.logo_url,
  ogLocale: "nl",
  ogLocaleAlternate: "en",
});

useHead({
  htmlAttrs: {
    lang: 'nl'
  },
  link: [
    {
      rel: "canonical",
      href: runtimeConfig.public.baseUrl + "/nl/vliegtuigen/" + data.value.slug
    },
    {
      rel: "alternate",
      hreflang: "x-default",
      href: runtimeConfig.public.baseUrl + "/aircrafts/" + data.value.slug
    },
    {
      rel: "alternate",
      hreflang: "en",
      href: runtimeConfig.public.baseUrl + "/aircrafts/" + data.value.slug
    },
    {
      rel: "alternate",
      hreflang: "nl",
      href: runtimeConfig.public.baseUrl + "/nl/vliegtuigen/" + data.value.slug
    }
  ],
});

useJsonld(() => {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: runtimeConfig.public.baseUrl + '/nl',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Luchtvaartmaatschappijen',
            item: runtimeConfig.public.baseUrl + '/nl/luchtvaartmaatschappijen',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value.owner.name,
            item: runtimeConfig.public.baseUrl + '/nl/luchtvaartmaatschappijen/' + data.value.owner.slug,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value.registration + ' - ' + data.value.model,
          },
        ],
      },
      {
        '@type': 'Vehicle',
        name: data.value.model,
        model: data.value.model.replace(data.value.manufacturer,''),
        manufacturer: {
          '@type': 'Organization',
          name: data.value.manufacturer,
        },
        category: 'Aircraft',
        productionDate: data.value.age,
        fuelType: 'Aviation gasoline',
        productID: data.value.registration,
      }
    ]
  }
});

const lastFlight = computed(() => {
  return data.value.flights.find(flight => flight.is_live || flight.is_landed) || null;
});

function formatDate(date: string | null) {
  if (date === null) {
    return null;
  }

  const latestFlightDate = moment(date);
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');

  if (latestFlightDate.isSame(today, 'day')) {
    return 'Vandaag';
  } else if (latestFlightDate.isSame(yesterday, 'day')) {
    return 'Gisteren';
  } else {
    return latestFlightDate.format('D/M/Y');
  }
}

function formatAge(age: string | null) {
  if (age === null) {
    return '-';
  }

  const ageDate = moment(age);
  const now = moment();

  return now.diff(ageDate, 'years');
}
</script>